import { setMediaUrls } from '../slices/mediaSlice';
import { apiSlice } from './apiSlice';
import { CatalogueResponse, PopularEventsResponse } from '@/types/MatchData';

export interface Competition {
  id: number;
  externalId: string;
  name: string;
  sport: string;
  startDate: string | null;
  endDate: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface CompetitionsResponse {
  success: boolean;
  message: string;
  competitions: Competition[];
}

export interface MatchEvent {
  matchId: string;
  sportId: string;
  seriesId: string;
  name: string;
  matchDateTime: string;
  isSubscribed: boolean;
  is_ended: boolean;
  competition_name: string;
}

export interface ScorecardApiResponse {
  success: boolean;
  message: string;
  scorecardUrl?: string;
  liveTvUrl?: string;
}

// Root API Response
export interface FixtureResponse {
  success: boolean;
  message: string;
  fixture: Fixture[];
}

export interface MarketPrice {
  price: number;
  size: number;
}

export interface Fixture {
  id: number;
  competitionId: number;
  providerId: number | null;
  externalId: string;
  name: string;
  sport: string;
  startTime: string;
  status: string;
  fancyCount: number;
  isFancy: boolean;
  isBookmaker: boolean;
  isPremiumFancy: boolean;
  isPopular: boolean;
  isFavorite?: boolean;
  inplay: boolean;

  competition: {
    id: number;
    externalId: string;
    name: string;
  };
  lay: MarketPrice[];
  back: MarketPrice[];
  runners: {
    handicap: number;
    runnerName: string;
    selectionId: string;
    sortPriority: string;
    backPrice1: number;
    backPrice2: number;
    backPrice3: number;
    layPrice1: number;
    layPrice2: number;
    layPrice3: number;
    backSize1: number;
    backSize2: number;
    backSize3: number;
    laySize1: number;
    laySize2: number;
    laySize3: number;
  }[];
}

export interface SearchEventsItem {
  id: string;
  name: string;
  sport: string;
  startTime: string;
  status: string;
}

export interface EventsBySport {
  [sport: string]: SearchEventsItem[];
}

export interface SearchEventsResponse {
  total: number;
  data: EventsBySport;
}

export interface SportMetaDetails {
  sport: string;
  total: number;
  inplay: number;
}

export interface MetaDetails {
  success: boolean;
  message: string;
  fixture: {
    sportCounts: SportMetaDetails[];
  };
}

export interface FavoriteEvent {
  id: string;
  name: string;
}

export interface FavoriteGamesResponse {
  message: string;
  games: {
    events: FavoriteEvent[];
  };
}

export const sportsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSportCompetitions: builder.query<CompetitionsResponse, string>({
      query: (sportName) => ({
        url: `/competitions?`,
        method: 'GET',
        params: { sport: sportName == 'Football' ? 'Soccer' : sportName },
      }),
      providesTags: ['Sports'],
    }),
    getPopularEvents: builder.query<PopularEventsResponse, void>({
      query: () => ({
        url: 'events/popular',
        method: 'GET',
      }),
      providesTags: ['Sports'],
    }),

    getMatches: builder.query<
      FixtureResponse,
      {
        sportName?: string;
        inplay?: boolean;
        seriesId?: string;
        search?: string;
        status?: string;
      }
    >({
      query: ({ inplay, sportName, seriesId, search, status }) => ({
        url: `/fixture`,
        params: {
          sport: sportName,
          competitionId: seriesId,
          search,
          inplay,
          status,
        },
      }),
      providesTags: ['Sports'],
    }),
    getMatcheDetails: builder.query<CatalogueResponse, { eventId: string }>({
      query: ({ eventId }) => {
        let url = `/catalogue/${eventId}`;
        return url;
      },
      providesTags: ['Sports'],
    }),
    getMediaUrls: builder.query<ScorecardApiResponse, string>({
      query: (eventId) => ({
        url: `events/scorecard/${eventId}`,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setMediaUrls({ scorecardUrl: data?.scorecardUrl, liveTvUrl: data?.liveTvUrl }));
        } catch (error: any) {
          console.error('Failed to fetch user details:', error);
        }
      },
      providesTags: ['Sports'],
    }),
    getEventsBySearch: builder.query<
      SearchEventsResponse,
      { search: string; fromDate: string; toDate: string }
    >({
      query: ({ search, fromDate, toDate }) => ({
        url: `events/search`,
        params: {
          search,
          fromDate,
          toDate,
        },
      }),
      providesTags: ['Sports'],
    }),
    getSportsMetaDetails: builder.query<MetaDetails, void>({
      query: () => ({
        url: '/fixture/metadata',
      }),
      providesTags: ['Sports'],
    }),
    updateFavourite: builder.mutation<any, { id: number; status: string }>({
      query: ({ id, status }) => ({
        url: `/events/favorite/${id}`,
        method: 'PUT',
        body: { status },
      }),
      invalidatesTags: ['Sports'],
    }),
    getFavourites: builder.query<FavoriteGamesResponse, void>({
      query: () => '/events/favorite',
      providesTags: ['Sports'],
    }),
    getFavouriteEventsFixture: builder.query<FixtureResponse, void>({
      query: () => '/fixture/favoriteevent',
      providesTags: ['Sports'],
    }),
  }),
});

export const {
  useGetSportCompetitionsQuery,
  useGetMatchesQuery,
  useLazyGetMatchesQuery,
  useGetMatcheDetailsQuery,
  useGetMediaUrlsQuery,
  useGetPopularEventsQuery,
  useLazyGetSportCompetitionsQuery,
  useGetEventsBySearchQuery,
  useLazyGetEventsBySearchQuery,
  useGetSportsMetaDetailsQuery,
  useUpdateFavouriteMutation,
  useGetFavouritesQuery,
  useGetFavouriteEventsFixtureQuery,
} = sportsApi;
